/**
 * ServiceHealthChecker — Verificación pre-batch de servicios online
 *
 * Ejecuta GET real (no HEAD) contra cada servicio online para detectar
 * degradación antes de procesar el batch. Alimenta CircuitBreakerManager
 * llamando recordFailure() x failureThreshold para abrir el CB de inmediato.
 *
 * @module services/geocoding/orchestrator/ServiceHealthChecker
 * @version 1.0.0 — F9.01a-fix.2
 */

import type { CircuitBreakerManager } from './CircuitBreakerManager';
import { acquireHostSlot } from '../HostRateGate';

// ============================================================================
// TIPOS
// ============================================================================

export interface HealthCheckResult {
  service: string;
  available: boolean;
  latencyMs: number;
  error?: string;
}

export interface HealthCheckOptions {
  /** Timeout por servicio en ms (default: 5000) */
  timeout?: number;
}

// ============================================================================
// ENDPOINTS DE HEALTH CHECK
// ============================================================================

/** GET real ligero por servicio — mínimo payload posible */
const HEALTH_CHECK_ENDPOINTS: Record<string, string> = {
  CDAU_ONLINE: 'https://www.callejerodeandalucia.es/servicios/cdau/wfs?service=WFS&request=GetCapabilities',
  CARTOCIUDAD: 'https://www.cartociudad.es/geocoder/api/geocoder/candidates?q=test&limit=1',
  NOMINATIM: 'https://nominatim.openstreetmap.org/search?q=test&format=json&limit=1',
};

const DEFAULT_TIMEOUT = 5_000;

// ============================================================================
// SERVICE HEALTH CHECKER
// ============================================================================

export class ServiceHealthChecker {
  /**
   * Verifica disponibilidad de servicios online con GET real.
   *
   * - Servicios con CB ya abierto se saltan (no se repite check)
   * - Si falla → llama recordFailure() x failureThreshold para abrir CB inmediato
   * - Timeout por check: 5 s (máximo ~15 s pre-batch para 3 servicios)
   */
  async checkServices(
    circuitBreaker: CircuitBreakerManager,
    options?: HealthCheckOptions
  ): Promise<HealthCheckResult[]> {
    const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
    const results: HealthCheckResult[] = [];

    const checks = Object.entries(HEALTH_CHECK_ENDPOINTS).map(
      async ([service, url]): Promise<HealthCheckResult> => {
        // Skip servicios con CB ya abierto
        if (!circuitBreaker.isAvailable(service)) {
          return { service, available: false, latencyMs: 0, error: 'circuit_already_open' };
        }

        const start = performance.now();
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeout);

        try {
          // FASE-A: la sonda a Nominatim es un GET real a /search y cuenta
          // contra su 1 req/s — pasa por la compuerta compartida. Hosts sin
          // límite registrado (CDAU, CartoCiudad) atraviesan sin espera.
          await acquireHostSlot(url);

          const response = await fetch(url, {
            method: 'GET',
            signal: controller.signal,
          });

          clearTimeout(timer);
          const latencyMs = Math.round(performance.now() - start);

          if (response.ok || response.status < 500) {
            circuitBreaker.recordSuccess(service);
            return { service, available: true, latencyMs };
          }

          // HTTP 5xx → servicio degradado
          this.forceOpenCircuit(circuitBreaker, service);
          return { service, available: false, latencyMs, error: `HTTP ${response.status}` };
        } catch (err) {
          clearTimeout(timer);
          const latencyMs = Math.round(performance.now() - start);
          const errorMsg = err instanceof DOMException && err.name === 'AbortError'
            ? `timeout_${timeout}ms`
            : String(err);

          this.forceOpenCircuit(circuitBreaker, service);
          return { service, available: false, latencyMs, error: errorMsg };
        }
      }
    );

    const settled = await Promise.allSettled(checks);
    for (const s of settled) {
      if (s.status === 'fulfilled') results.push(s.value);
    }

    return results;
  }

  /**
   * Fuerza apertura del CB llamando recordFailure() tantas veces como
   * failureThreshold (default 2). Así el batch arranca con el servicio
   * ya deshabilitado sin esperar fallos reales.
   */
  private forceOpenCircuit(cb: CircuitBreakerManager, service: string): void {
    // Default failureThreshold es 2 → 2 llamadas abren el circuito
    cb.recordFailure(service);
    cb.recordFailure(service);
  }
}
